/**
 * Kitchen measures for amounts in grams:
 * - the readout: some grams of a food as the kitchen says them, "≈ 1⅔ cups", "≈ ½ cup (8 tbsp)",
 *   "≈ 2 large eggs", "≈ 5¼ oz" or "≈ 350 ml", in cups and spoons or metric;
 * - conversions: grams to and from an amount in a unit (cup, tbsp, tsp, oz, ml), and the − and +
 *   steps in each;
 * - typed amounts: "1 1/2 cups", "6 oz" or "2 large" into grams, from the food's own sizes.
 *
 * Grams stay the base unit (ml for drinks). A food's volume comes only from its own volume sizes
 * ("1 cup (227 g)", "1 tbsp (14 g)"), or from its being a drink, whose amounts are ml. A density
 * is never guessed: 1 g per ml is off by about 2× for flour. A unit reads the way amountForFood
 * reads it in a recipe line: the food's own size in that unit when it has one, else its density
 * from its largest volume size. Shared and pure, so every amount control and the recipe editor
 * read amounts the same way.
 */
import { sizeLabel, type Serving } from './foods';
import {
  amountForFood,
  bestSize,
  isMass,
  isSize,
  isVolume,
  largestVolume,
  normalizeFractions,
  parseIngredientLine,
  sizesOf,
  VOLUME_ML,
  WEIGHT_G,
  type FoodSize,
  type FoodSizes,
  type Unit,
} from './ingredientLine';
import { parseDecimal } from './numbers';
import { MAX_ENTRY_GRAMS } from './recipes';
import type { KitchenUnits } from './units';

// ——— Units

/** Kitchen units: what an amount can be shown in besides the food's own unit. A recipe ingredient keeps one. */
export const KITCHEN_AMOUNT_UNITS = ['oz', 'cup', 'tbsp', 'tsp', 'ml'] as const;
export type KitchenUnit = (typeof KITCHEN_AMOUNT_UNITS)[number];
/** The units an amount can be entered in. 'g' is the food's own unit: grams, or ml for a drink. */
export type AmountUnit = 'g' | KitchenUnit;
export const AMOUNT_UNITS: readonly AmountUnit[] = ['g', ...KITCHEN_AMOUNT_UNITS];

export const isKitchenUnit = (value: unknown): value is KitchenUnit => typeof value === 'string' && (KITCHEN_AMOUNT_UNITS as readonly string[]).includes(value);

/** What − and + move by in each unit. */
export const UNIT_STEPS: Readonly<Record<AmountUnit, number>> = { g: 1, oz: 0.5, cup: 0.25, tbsp: 1, tsp: 0.5, ml: 5 };

const UNIT_NAMES: Readonly<Record<AmountUnit, string>> = { g: 'grams', oz: 'ounces', cup: 'cups', tbsp: 'tablespoons', tsp: 'teaspoons', ml: 'milliliters' };

/** A unit as it reads aloud: "Amount in cups". A drink's own unit is milliliters. */
export function unitName(unit: AmountUnit, liquid = false): string {
  return unit === 'g' && liquid ? UNIT_NAMES.ml : UNIT_NAMES[unit];
}

/** A unit as it's shown: "g" ("ml" for a drink), "oz", "cup" (up to 1) or "cups", "tbsp", "tsp", "ml". */
export function unitSymbol(unit: AmountUnit, liquid = false, amount?: number): string {
  if (unit === 'g') return liquid ? 'ml' : 'g';
  if (unit === 'cup') return amount !== undefined && amount <= 1 ? 'cup' : 'cups';
  return unit;
}

/** Amounts are kept to 0.1 g, so this is the least that can be logged. */
export const LEAST_GRAMS = 0.1;

/** Grams as an amount keeps them: to 0.1 g, from 0.1 g to one entry's limit. */
export const keepGrams = (grams: number) => Math.min(MAX_ENTRY_GRAMS, Math.max(LEAST_GRAMS, Math.round(grams * 10) / 10));

/** An amount in a kitchen unit as a field shows it: at most 2 decimals, without trailing zeros ("<0.01" for a speck). */
export function unitAmountText(amount: number): string {
  const shown = Math.round(amount * 100) / 100;
  return shown === 0 && amount > 0 ? '<0.01' : String(shown);
}

// ——— Converting

const NO_WORDS: ReadonlySet<string> = new Set();

/** One of a unit, for a food: its grams, and the size that says so ("1/4 cup shredded (28 g)"). */
interface PerUnit {
  grams: number;
  size: FoodSize | null;
}

/** Volumes too rough to tell a density by: a pinch, a dash and a drop are habits, not measures. */
const ROUGH_VOLUMES: ReadonlySet<Unit> = new Set(['pinch', 'dash', 'drop']);

/** The largest volume size (amountForFood's rule), leaving out a pinch, a dash or a drop. */
const measuredVolume = (sizes: readonly FoodSize[]) => largestVolume(sizes.filter((s) => !(s.unit && ROUGH_VOLUMES.has(s.unit))));

/** Grams per ml from the largest measured volume size, or 1 for a drink without one, whose amounts are ml. */
function perMl(sizes: readonly FoodSize[], liquid: boolean | undefined): PerUnit | null {
  const volume = measuredVolume(sizes);
  if (volume) return { grams: volume.grams / (volume.quantity * VOLUME_ML[volume.unit]), size: volume };
  return liquid ? { grams: 1, size: null } : null;
}

/**
 * One `unit` of a food, as amountForFood reads "1 tbsp": its own size in that unit when it has
 * one ("2 tbsp (30 g)" makes a tbsp 15 g), else through its density. An ounce weighs the same
 * for every food but drinks, whose ounces would be fluid ones. Null when the food can't convert it.
 */
function perUnit(unit: AmountUnit, food: FoodSizes, sizes: readonly FoodSize[] = sizesOf(food.servings)): PerUnit | null {
  if (unit === 'g') return { grams: 1, size: null };
  if (unit === 'oz') return food.liquid ? null : { grams: WEIGHT_G.oz, size: null };
  if (unit === 'ml' && food.liquid) return { grams: 1, size: null };
  const own = bestSize(
    sizes.filter((s) => s.unit === unit),
    NO_WORDS,
  );
  if (own) return { grams: own.grams / own.quantity, size: own };
  const ml = perMl(sizes, food.liquid);
  return ml && { grams: ml.grams * VOLUME_ML[unit], size: ml.size };
}

/** Grams in one ml of a food, from its own volume sizes, or 1 for a drink. Null when nothing says: a density is never guessed. */
export function densityOf(food: FoodSizes): number | null {
  return perMl(sizesOf(food.servings), food.liquid)?.grams ?? null;
}

/** Grams in one of `unit`, or null when the food can't convert it. */
export function gramsPerUnit(unit: AmountUnit, food: FoodSizes): number | null {
  return perUnit(unit, food)?.grams ?? null;
}

/** Grams in `amount` of `unit` (not yet kept to 0.1 g), or null when the food can't convert it. */
export function toGrams(amount: number, unit: AmountUnit, food: FoodSizes): number | null {
  const per = gramsPerUnit(unit, food);
  return per === null ? null : amount * per;
}

/** `grams` in `unit`, or null when the food can't convert it. */
export function fromGrams(grams: number, unit: AmountUnit, food: FoodSizes): number | null {
  const per = gramsPerUnit(unit, food);
  return per === null || !(per > 0) ? null : grams / per;
}

/**
 * `grams` in `unit` as an amount field shows it: on a whole step (¼ cup, 1 tbsp, ½ tsp, ½ oz,
 * 5 ml) when the grams are that step's, kept to 0.1 g. 331 g of Greek yogurt is 350 ml, not
 * 349.96. Null when the food can't convert the unit.
 */
export function amountIn(grams: number, unit: AmountUnit, food: FoodSizes): number | null {
  const per = gramsPerUnit(unit, food);
  if (per === null || !(per > 0)) return null;
  const amount = grams / per;
  if (unit === 'g') return amount;
  const step = UNIT_STEPS[unit];
  const nearest = Math.round(amount / step) * step;
  return nearest > 0 && Math.abs(amount - nearest) <= 0.05 / per + 1e-9 ? nearest : amount;
}

/**
 * The units a food's amount can be entered in, its own unit first. Cups and spoons: grams (ml for
 * a drink), ounces for what's weighed, and cup, tbsp and tsp when its volume is known. Metric:
 * grams, and ml when its volume is known (a drink is in ml already).
 */
export function unitsFor(food: FoodSizes, units: KitchenUnits): AmountUnit[] {
  const volume = densityOf(food) !== null;
  if (units === 'metric') return volume && !food.liquid ? ['g', 'ml'] : ['g'];
  const out: AmountUnit[] = ['g'];
  if (!food.liquid) out.push('oz');
  if (volume) out.push('cup', 'tbsp', 'tsp');
  return out;
}

/**
 * − or + from `amount` of a kitchen unit: the next whole step below or above it (¼ cup, 1 tbsp,
 * ½ tsp, ½ oz, 5 ml), with its grams kept to 0.1 g up to the limit. It steps from the amount as
 * a field shows it (2 decimals: 0.7489 cups shows 0.75, so + goes to 1), and an amount within
 * rounding of a step (grams are kept to 0.1 g) counts as that step. − stops at one step: null
 * there, and for a unit the food can't convert.
 */
export function stepAmount(amount: number, unit: KitchenUnit, direction: 1 | -1, food: FoodSizes): { amount: number; grams: number } | null {
  const per = gramsPerUnit(unit, food);
  if (per === null || !(per > 0)) return null;
  const step = UNIT_STEPS[unit];
  const shown = Math.round(amount * 100) / 100;
  const slack = 0.05 / per + 1e-9;
  const next = direction > 0 ? (Math.floor((shown + slack) / step) + 1) * step : (Math.ceil((shown - slack) / step) - 1) * step;
  if (next < step - 1e-9) return null;
  const grams = keepGrams(next * per);
  return { amount: next * per > MAX_ENTRY_GRAMS ? grams / per : next, grams };
}

// ——— Reading amounts out

/** Fraction glyphs for the readout. */
const GLYPHS: ReadonlyArray<readonly [number, string]> = [
  [1 / 8, '⅛'],
  [1 / 4, '¼'],
  [1 / 3, '⅓'],
  [3 / 8, '⅜'],
  [1 / 2, '½'],
  [5 / 8, '⅝'],
  [2 / 3, '⅔'],
  [3 / 4, '¾'],
  [7 / 8, '⅞'],
];

/** 1.5 → "1½", 0.25 → "¼", 3 → "3". An amount between the glyphs reads as a decimal ("1.4"). */
export function fractionText(n: number): string {
  const whole = Math.floor(n + 1e-9);
  const rest = n - whole;
  if (rest < 1e-6) return String(whole);
  const glyph = GLYPHS.find(([v]) => Math.abs(v - rest) < 1e-6)?.[1];
  if (!glyph) return String(Math.round(n * 100) / 100);
  return whole === 0 ? glyph : `${whole}${glyph}`;
}

const CUP_PARTS = [1 / 8, 1 / 4, 1 / 3, 1 / 2, 2 / 3, 3 / 4];
const HALVES = [1 / 2];
const QUARTERS = [1 / 4, 1 / 2, 3 / 4];
const EIGHTHS = [1 / 8, 1 / 4, 3 / 8, 1 / 2];

/** `n` to the nearest whole number plus one of `parts` (fractions of one). A tie goes up. */
function roundTo(n: number, parts: readonly number[]): number {
  const whole = Math.floor(n);
  const rest = n - whole;
  let best = 0;
  for (const p of [...parts, 1]) if (Math.abs(rest - p) <= Math.abs(rest - best) + 1e-12) best = p;
  return whole + best;
}

/** Words that say how a volume size was measured ("1 cup diced", "1/4 cup shredded"): the density depends on them. */
const MEASURED_AS = new Set([
  'chopped',
  'diced',
  'cubed',
  'cubes',
  'sliced',
  'slices',
  'shredded',
  'grated',
  'crumbled',
  'crumbles',
  'mashed',
  'pureed',
  'puréed',
  'riced',
  'minced',
  'ground',
  'crushed',
  'flaked',
  'packed',
  'sifted',
  'melted',
  'dry',
  'uncooked',
  'cooked',
  'raw',
  'whole',
  'halves',
  'halved',
  'pieces',
  'chunks',
  'sections',
  'segments',
  'florets',
  'kernels',
  'shelled',
  'pitted',
  'frozen',
  'drained',
]);

/** " shredded" when a size's words say how it was measured (two at most), else nothing. */
function measuredAs(size: FoodSize | null): string {
  const words = size?.text.toLowerCase().split(' ').filter(Boolean) ?? [];
  return words.length > 0 && words.length <= 2 && words.every((w) => MEASURED_AS.has(w)) ? ` ${words.join(' ')}` : '';
}

/** Cups from ¼ cup (with tbsp in brackets up to ½ cup), then tbsp from 1 tbsp, then tsp. */
function cupsAndSpoons(grams: number, food: FoodSizes, sizes: readonly FoodSize[]): string | null {
  const cup = perUnit('cup', food, sizes);
  const tbsp = perUnit('tbsp', food, sizes);
  const tsp = perUnit('tsp', food, sizes);
  if (!cup || !tbsp || !tsp) return null;
  const cups = grams / cup.grams;
  if (cups >= 1 / 4 - 1e-9) {
    const n = roundTo(cups, CUP_PARTS);
    // Small cup measures read in spoons too: a stick of butter is marked in tbsp, and a ⅓ cup
    // measure isn't always to hand. The spoons are the same cups (16 tbsp to a cup), so a food
    // whose own sizes disagree never reads "½ cup (10 tbsp)".
    const spoons = n <= 1 / 2 ? ` (${fractionText(roundTo(cups * 16, HALVES))} tbsp)` : '';
    return `≈ ${fractionText(n)} ${n <= 1 ? 'cup' : 'cups'}${measuredAs(cup.size)}${spoons}`;
  }
  const spoons = grams / tbsp.grams;
  if (spoons >= 1 - 1e-9) return `≈ ${fractionText(roundTo(spoons, HALVES))} tbsp${measuredAs(tbsp.size)}`;
  const teaspoons = grams / tsp.grams;
  const n = roundTo(teaspoons, teaspoons < 1 / 2 ? EIGHTHS : QUARTERS);
  return n > 0 ? `≈ ${fractionText(n)} tsp${measuredAs(tsp.size)}` : 'less than ⅛ tsp';
}

/** Millilitres, to 5 ml under 100 ml and to 10 ml from there. */
function millilitres(grams: number, food: FoodSizes, sizes: readonly FoodSize[]): string | null {
  const ml = perUnit('ml', food, sizes);
  if (!ml) return null;
  const amount = grams / ml.grams;
  const step = amount < 100 ? 5 : 10;
  const n = Math.round(amount / step) * step;
  return n > 0 ? `≈ ${n} ml${measuredAs(ml.size)}` : 'less than 5 ml';
}

/** Ounces, to ¼ oz under 8 oz (half a pound) and to ½ oz from there. */
function ounces(grams: number): string {
  const oz = grams / WEIGHT_G.oz;
  const n = roundTo(oz, oz < 8 ? QUARTERS : HALVES);
  return n > 0 ? `≈ ${fractionText(n)} oz` : 'less than ¼ oz';
}

// ——— Counting

/** Size words, which aren't the thing counted: "1 medium" doesn't make "2 mediums". */
const SIZE_WORDS = new Set(['small', 'medium', 'med', 'large', 'lg', 'big', 'jumbo', 'xl', 'mini', 'regular']);
/** Words whose plural is the same word: "10 large shrimp". */
const SAME_PLURAL = new Set(['fish', 'shrimp', 'sheep', 'deer', 'moose', 'squid', 'salmon', 'trout', 'tuna', 'cod', 'halibut', 'bison']);
/** Words for what isn't counted one by one: "1 bread" doesn't make "2 breads". */
const UNCOUNTED = new Set(['bread', 'rice', 'corn']);
const ES_AFTER_O = new Set(['potato', 'tomato', 'mango', 'hero']);

/** "egg" → "eggs", "patty" → "patties", "sandwich" → "sandwiches". Null when English makes it hard to be sure. */
function pluralWord(word: string): string | null {
  const w = word.toLowerCase();
  if (SIZE_WORDS.has(w) || UNCOUNTED.has(w) || w.length < 2) return null;
  if (SAME_PLURAL.has(w)) return word;
  if (/(?:ss|x|z|ch|sh)$/.test(w)) return `${word}es`;
  // Already plural, or a word like "hummus": either way, not something to add an "s" to.
  if (w.endsWith('s')) return null;
  // "half" → "halves", "loaf" → "loaves".
  if (/fe?$/.test(w)) return null;
  if (/[^aeiou]y$/.test(w)) return `${word.slice(0, -1)}ies`;
  if (w.endsWith('o')) return ES_AFTER_O.has(w) ? `${word}es` : `${word}s`;
  return `${word}s`;
}

/** "eggs" → "egg", "slices" → "slice", "tomatoes" → "tomato". Null when it can't be sure ("cookies": cookie, or cooky?). */
function singularWord(word: string, known: ReadonlySet<string>): string | null {
  const w = word.toLowerCase();
  if (SAME_PLURAL.has(w)) return word;
  if (w.endsWith('ies')) {
    // "cookies" could be cookie or cooky: another of the food's sizes says which.
    const choices = [`${word.slice(0, -3)}y`, word.slice(0, -1)].filter((stem) => known.has(stem.toLowerCase()));
    return choices.length === 1 ? choices[0]! : null;
  }
  // "halves" → half, "loaves" → loaf.
  if (w.endsWith('ves') || !w.endsWith('s') || w.endsWith('ss')) return null;
  // The one whose plural gives the word back: "tomatoes" is tomato, "slices" slice.
  return [word.slice(0, -2), word.slice(0, -1)].find((stem) => stem.length > 1 && pluralWord(stem)?.toLowerCase() === w) ?? null;
}

/** A measure in brackets at the end of a label: "(55 g)", "(240 ml)", "(8 oz)". */
const MEASURE_BRACKET = /\s*\(\s*(?:about\s+|approx\.?\s+|~\s*)?\d+(?:[.,]\d+)?\s*(?:g|grams?|ml|oz|ounces?|lbs?|fl\.?\s*oz)\s*\)\s*$/i;

/** A size in a label's tail that describes each one without being an amount of it: "8 in", "6 1/2 in". */
const DIMENSION = /(?:\d+(?:[.,]\d+)?(?:\s+\d+\s*\/\s*\d+)?|\d+\s*\/\s*\d+)\s*(?:inch(?:es)?\b|in\b|cm\b|mm\b|["″”])/gi;

/** A size as something counted: one of it and many, from its label ("1 large egg", "2 links", "1 tortilla, 8 in"). */
interface Counted {
  serving: Serving;
  /** Grams in one of the things counted: "2 links (46 g)" is 23 g a link. */
  each: number;
  /** "large egg" and "large eggs", with what follows a comma kept: "tortilla, 8 in". Null when the label doesn't pluralise simply. */
  one: string | null;
  many: string | null;
  /** What follows the comma is an amount of each one ("steak, 6 oz", "roll, 8 pieces"), which a plural would read as the total. */
  eachAmount: boolean;
}

/** The parts of a label like "1 large egg (50 g)" or "6 wing pieces, edible part": how many, the thing counted, and the rest. */
function labelParts(label: string): { count: number; head: string; tail: string } | null {
  const m = /^(\d+)\s+([^,]+?)(\s*,.*)?$/.exec(label.trim().replace(MEASURE_BRACKET, ''));
  if (!m) return null;
  const head = m[2]!.trim();
  // Only words: no numbers, brackets or "of" to work around ("1 slice of bread").
  if (!/^\p{L}[\p{L}\s'-]*$/u.test(head) || /\s(?:of|or|and|with|per|each)\s/i.test(` ${head} `)) return null;
  return { count: Number(m[1]), head, tail: m[3] ?? '' };
}

/** The first size that counts the food itself (not a weight or a volume), read as one and many when its label allows. */
function countedSize(food: FoodSizes, sizes: readonly FoodSize[]): Counted | null {
  const servings = food.servings.filter((s) => s.grams > 0);
  const counts = servings.flatMap((serving, i) => {
    const size = sizes[i];
    return size && !isVolume(size.unit) && !isMass(size.unit) ? [{ serving, size }] : [];
  });
  const first = counts[0];
  if (!first) return null;
  const parts = labelParts(first.serving.label);
  if (parts && parts.count >= 1) {
    const words = parts.head.split(/\s+/);
    const last = words.at(-1)!;
    const front = words.slice(0, -1).join(' ');
    const join = (word: string) => `${front ? `${front} ` : ''}${word}${parts.tail}`;
    const eachAmount = /\d/.test(parts.tail.replace(DIMENSION, ''));
    if (parts.count === 1) {
      const many = pluralWord(last);
      if (many) return { serving: first.serving, each: first.serving.grams, one: join(last), many: join(many), eachAmount };
    } else {
      // A one-of label among the others ("1 cookie (11 g)") settles a plural like "cookies".
      const known = new Set(
        counts.flatMap(({ serving }) => {
          const p = labelParts(serving.label);
          return p?.count === 1 ? [p.head.split(/\s+/).at(-1)!.toLowerCase()] : [];
        }),
      );
      const one = singularWord(last, known);
      if (one) return { serving: first.serving, each: first.serving.grams / parts.count, one: join(one), many: join(last), eachAmount };
    }
  }
  return { serving: first.serving, each: first.serving.grams, one: null, many: null, eachAmount: false };
}

/** A count to the nearest quarter under one, half to ten, and whole number from there. */
const roundCount = (n: number) => roundTo(n, n < 1 ? QUARTERS : n < 10 ? HALVES : []);

/**
 * "≈ 2 large eggs" when the label pluralises simply, else "≈ 1½ × 1 medium (118 g)", with "=" for
 * an exact whole or half multiple of the size (within 0.5 g). Null under an eighth of one.
 */
function counting(grams: number, c: Counted, liquid: boolean | undefined): string | null {
  if (c.one !== null && c.many !== null) {
    const r = roundCount(grams / c.each);
    if (r <= 0) return null;
    // "2 steaks, 6 oz" would read as 6 oz in all: other than one, such a size counts as multiples of it.
    if (r === 1 || !c.eachAmount) return `≈ ${fractionText(r)} ${r <= 1 ? c.one : c.many}`;
  }
  const r = roundCount(grams / c.serving.grams);
  if (r <= 0) return null;
  const label = sizeLabel(c.serving, liquid);
  const exact = r * 2 === Math.round(r * 2) && Math.abs(grams - r * c.serving.grams) < 0.5;
  return `${exact ? '=' : '≈'} ${r === 1 ? label : `${fractionText(r)} × ${label}`}`;
}

/**
 * Some grams of a food (ml for a drink) as the kitchen says them, or null when there's nothing honest to say.
 *
 * Cups and spoons, when the food's volume is known: cups from ¼ cup, to the nearest ⅛ ¼ ⅓ ½ ⅔ ¾
 * (with tbsp in brackets up to ½ cup, "≈ ¼ cup (4 tbsp)"); tbsp from 1 tbsp, to ½; tsp below,
 * to ¼ (⅛ under ½ tsp). Without a volume: counts of its sizes ("≈ 2 large eggs"), else ounces,
 * to ¼ oz under 8 oz and ½ oz from there ("≈ 5¼ oz"). Metric: millilitres, to 5 ml under 100 and
 * 10 ml from there ("≈ 350 ml"), else counts; never ounces, and nothing for a drink, which is
 * in ml already. Kitchen conversions say "≈"; "=" is kept for an exact multiple of a size.
 */
export function kitchenReadout(grams: number, food: FoodSizes, units: KitchenUnits): string | null {
  if (!Number.isFinite(grams) || grams <= 0) return null;
  const sizes = sizesOf(food.servings);
  const volume = perMl(sizes, food.liquid) !== null;
  if (units === 'us' && volume) return cupsAndSpoons(grams, food, sizes);
  if (units === 'metric' && volume && !food.liquid) return millilitres(grams, food, sizes);
  const counted = measuredVolume(sizes) ? null : countedSize(food, sizes);
  if (counted) return counting(grams, counted, food.liquid);
  return units === 'us' && !food.liquid ? ounces(grams) : null;
}

// ——— Typed amounts

/** A typed amount: grams (and the number typed, when it was just a number), or a note on why not. */
export type TypedAmount = { ok: true; grams: number; amount: number | null } | { ok: false; note: string | null };

/** "1.5", "1 1/2", "1½", "57,5" or "2,300": a number without a unit, read the way the app reads numbers (parseDecimal). */
const plainNumber = (text: string) => parseDecimal(normalizeFractions(text));

/** A parsed unit as a note names it: "cup", "large", "can", "liter". */
const unitWord = (unit: Unit) => (unit === 'l' ? 'liter' : unit);

/** What the person can enter instead: "Enter grams or pick a size." */
function enterInstead(unit: AmountUnit, liquid: boolean | undefined): string {
  const what = unit === 'g' ? (liquid ? 'ml' : 'grams') : unit === 'ml' ? 'ml' : unitName(unit);
  return `Enter ${what} or pick a size.`;
}

/**
 * Grams from what someone typed in an amount field showing `unit`. A plain number is in that
 * unit ("150" is grams, or ml for a drink). A unit converts with the food's own sizes, as a recipe
 * line does: "1 1/2 cups", "6 oz", "2 large", "1 can" (a drink's ounces are fluid ounces). When
 * the food can't convert it, there's a note instead of a guess ("This food has no cup size.
 * Enter grams or pick a size."). Grams are kept to 0.1 g, between 0.1 g and one entry's limit,
 * and the number typed comes back only when its grams weren't cut to fit. Nothing typed has no
 * note: the field just goes back to its amount.
 */
export function parseTypedAmount(text: string, food: FoodSizes, unit: AmountUnit = 'g'): TypedAmount {
  const raw = text.trim();
  if (raw === '') return { ok: false, note: null };
  const unreadable = { ok: false, note: enterInstead(unit, food.liquid) } as const;
  // "-3" isn't an amount (and a leading dash would otherwise read as a list bullet).
  if (/^[-−–—]/.test(raw)) return unreadable;
  const plain = plainNumber(raw);
  if (plain !== null) {
    const grams = plain > 0 ? toGrams(plain, unit, food) : null;
    if (grams === null) return unreadable;
    const kept = keepGrams(grams);
    return { ok: true, grams: kept, amount: Math.abs(kept - grams) <= 0.05 + 1e-9 ? plain : null };
  }
  // Ounces of a drink are fluid ounces: "12 oz" of cola is a can.
  const typed = food.liquid ? raw.replace(/(\d\s*-?\s*)(?:ounces?|oz)\b\.?/gi, '$1fl oz') : raw;
  const line = parseIngredientLine(typed);
  if (line.quantity === null && line.grams === null && line.ml === null) return unreadable;
  // "0 cups" is no amount either.
  if (line.quantity !== null && !(line.quantity > 0) && line.grams === null && line.ml === null) return unreadable;
  const noSize = (what: Unit | null) => ({
    ok: false as const,
    note: `${what === null ? 'This food has no size to count.' : `This food has no ${unitWord(what)} size.`} ${enterInstead(unit, food.liquid)}`,
  });
  const sizes = sizesOf(food.servings);
  // A size the food doesn't come in: "2 large" of a banana sized "1 medium" isn't 2 medium ones.
  if (isSize(line.unit) && !sizes.some((s) => s.unit === line.unit)) return noSize(line.unit);
  // A drink's amounts are ml: 250 ml of it is 250.
  if (food.liquid && line.grams === null && line.ml !== null) return { ok: true, grams: keepGrams(line.ml), amount: null };
  // A volume needs the food's own size in that unit, or a density from a measured volume size
  // (amountForFood would take one from a pinch).
  const volume = line.grams === null && (isVolume(line.unit) || line.ml !== null);
  if (volume && !food.liquid && densityOf(food) === null && !sizes.some((s) => s.unit === line.unit)) return noSize(line.unit ?? 'ml');
  const read = amountForFood(typed, food);
  if (read.basis === 'guess') return noSize(line.unit);
  return { ok: true, grams: keepGrams(read.grams), amount: null };
}

// ——— Recipe ingredients

/**
 * The kitchen unit a recipe line is written in: "1/2 cup white sugar" is cups, "8 tablespoons of
 * butter" tbsp, "6 oz" ounces, "250 ml" ml. Pints, quarts, gallons and fluid ounces read as cups
 * (under 2 fl oz, as tbsp), liters as ml and pounds as ounces. Null for grams, counts and the
 * rest ("2 eggs", "a pinch").
 */
export function lineUnit(line: string): KitchenUnit | null {
  const { unit, quantity } = parseIngredientLine(line);
  switch (unit) {
    case 'fl oz':
      return quantity !== null && quantity < 2 ? 'tbsp' : 'cup';
    case 'cup':
    case 'pint':
    case 'quart':
    case 'gallon':
      return 'cup';
    case 'tbsp':
      return 'tbsp';
    case 'tsp':
      return 'tsp';
    case 'oz':
    case 'lb':
      return 'oz';
    case 'ml':
    case 'cl':
    case 'dl':
    case 'l':
      return 'ml';
    default:
      return null;
  }
}

/** `unit` when the food's amount control offers it in these kitchen measures, else null (grams). */
export function unitIfOffered(unit: AmountUnit | null | undefined, food: FoodSizes, units: KitchenUnits): KitchenUnit | null {
  return isKitchenUnit(unit) && unitsFor(food, units).includes(unit) ? unit : null;
}

/** Each unit's least amount a recipe writes, for "less than ⅛ cup". */
const LEAST_WRITTEN: Readonly<Record<KitchenUnit, number>> = { cup: 1 / 8, tbsp: 1 / 2, tsp: 1 / 8, oz: 1 / 4, ml: 5 };

/** An amount in a unit to what a recipe writes: cups to ⅛ ¼ ⅓ ½ ⅔ ¾, tbsp to ½, tsp to ¼ (⅛ under ½), oz to ¼ (½ from 8), ml to 5 (10 from 100). */
function written(amount: number, unit: KitchenUnit): number {
  switch (unit) {
    case 'cup':
      return roundTo(amount, CUP_PARTS);
    case 'tbsp':
      return roundTo(amount, HALVES);
    case 'tsp':
      return roundTo(amount, amount < 1 / 2 ? EIGHTHS : QUARTERS);
    case 'oz':
      return roundTo(amount, amount < 8 ? QUARTERS : HALVES);
    case 'ml': {
      const step = amount < 100 ? 5 : 10;
      return Math.round(amount / step) * step;
    }
  }
}

/**
 * Some grams in a unit as a recipe writes them: "½ cup", "3 tbsp", "6 oz", "350 ml", with "≈" when
 * that rounds them ("≈ 1⅔ cups"), and "less than ⅛ cup" for a speck. An amount within what keeping
 * grams to 0.1 g (and a line's "1/3" to 0.333) can move it is exact: "1/3 cup sugar" is ⅓ cup.
 * Null when the food can't convert the unit.
 */
export function unitAmountLabel(grams: number, unit: KitchenUnit, food: FoodSizes): string | null {
  const per = gramsPerUnit(unit, food);
  if (per === null || !(per > 0) || !Number.isFinite(grams) || grams <= 0) return null;
  const amount = amountIn(grams, unit, food)!;
  const n = written(amount, unit);
  if (n <= 0) return `less than ${fractionText(LEAST_WRITTEN[unit])} ${unitSymbol(unit, false, LEAST_WRITTEN[unit])}`;
  const exact = Math.abs(n - amount) <= 0.05 / per + 0.0005;
  return `${exact ? '' : '≈ '}${fractionText(n)} ${unitSymbol(unit, false, n)}`;
}
